'use client';

/**
 * Top-level API methods (auth, user, wallet, conferences, analytics, network).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L2249-L2759).
 *
 * These methods live directly on the `api` object (not inside a nested
 * namespace). They are exported as `userMethods` and spread into the
 * `api` object construction in `../index.ts`.
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import { tokenStore } from '../core/token-store';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const userMethods = {
async getFeatureFlags(signal?: AbortSignal): Promise<FeatureFlags> {
  return apiFetch<FeatureFlags>('/features', { auth: false, signal });
},
// Sprint 137 (Founder directive 2026-09-22) — Site Maintenance Mode.
// Public unauthenticated endpoint. Returns `{maintenance: boolean}`.
// Read by `useSiteMaintenance()` hook in AppShell to detect maintenance
// + redirect non-admin users to `/maintenance`. Exempt from the backend
// maintenance gate (see backend site-maintenance.middleware.ts
// EXEMPT_PATH_PREFIXES) so it always responds even when the gate is on.
// Anti-§6: pure read-only endpoint — NO business logic, NO auth required.
// Fail-closed: returns `{maintenance: false}` if the fetch fails (network
// error, 5xx, etc.) — matches GOBERNANZA-QA §8.3 (site operational by
// default). The hook treats `false` as "site operational" + skips the
// redirect.
async getSiteStatus(signal?: AbortSignal): Promise<{ maintenance: boolean }> {
  try {
    const data = await apiFetch<{ maintenance: boolean }>('/site-status', {
      auth: false,
      signal,
      // Short timeout (3s) so the hook doesn't block UI when the backend
      // is unreachable. The default READ_TIMEOUT_MS (8s) is too long for
      // a status check that should be near-instant.
      timeoutMs: 3_000,
    });
    return data;
  } catch {
    // Network error, timeout, or 5xx → assume site is operational
    // (fail-closed). The next poll cycle will retry. If maintenance is
    // actually on + the user is hitting a non-exempt endpoint, the
    // backend middleware will return 503 SITE_IN_MAINTENANCE on the
    // next API call → the AppShell can also detect that + redirect.
    return { maintenance: false };
  }
},
async register(input: RegisterRequest): Promise<AuthResponse> {
  const res = await apiFetch<AuthResponse>('/auth/register', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: JSON.stringify(input),
  });
  // Access token en memoria; refresh token ya está en cookie (set por backend).
  if (res.tokens?.accessToken) tokenStore.setAccess(res.tokens.accessToken);
  return res;
},
async login(input: LoginRequest): Promise<AuthResponse | Login2FAResponse> {
  const res = await apiFetch<AuthResponse | Login2FAResponse>('/auth/login', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: JSON.stringify(input),
  });
  // Si no es 2FA flow, guardar access token
  if ('tokens' in res && res.tokens?.accessToken) tokenStore.setAccess(res.tokens.accessToken);
  return res;
},
async logout(): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' });
  } finally {
    tokenStore.clear();
  }
},
async me(signal?: AbortSignal): Promise<PublicUser> {
  return apiFetch<PublicUser>('/auth/me', { signal });
},
async getDashboard(signal?: AbortSignal): Promise<DashboardData> {
  return apiFetch<DashboardData>('/dashboard', { signal });
},
// SPEC-008 §3.1.2 — Update profile (PUT /auth/me, endpoint #10)
// Bug 6 (SPEC-049): the `avatar` field is NO LONGER accepted here —
// use `uploadAvatar(file)` instead (POST /auth/me/avatar with multipart
// FormData). The backend PUT /auth/me rejects `avatar` payloads because
// the JSON body parser has a 256kb limit that base64-encoded avatars
// routinely exceed (was triggering 413 PayloadTooLongError → 500 in the
// old catch-all error middleware).
async updateProfile(data: UpdateProfileInput): Promise<PublicUser> {
  return apiFetch<PublicUser>('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
},
// Bug 6 (SPEC-049): avatar upload via dedicated endpoint with Multer
// memoryStorage on the backend (2MB limit, image MIME whitelist). The
// server returns `{ avatar: <dataUrl> }` — the same data URL shape the
// frontend used pre-Bug-6, so consumers don't need to change their
// render code.
async uploadAvatar(file: File): Promise<{ avatar: string }> {
  const formData = new FormData();
  formData.append('avatar', file);
  return apiFetch<{ avatar: string }>('/auth/me/avatar', {
    method: 'POST',
    body: formData,
    skipJsonStringify: true,
  });
},
// SPEC-095 (Profileia Hub + cover image): cover image upload via dedicated
// multipart endpoint. Mirrors the avatar upload pattern: the backend
// accepts a single `cover` file (Multer memoryStorage, 2MB limit, image
// MIME whitelist). The server returns `{ coverImage: <dataUrl> }` — the
// data URL is stored on `user.profile.coverImage` and rendered at the
// top of the user's public Profile Hub page (`/{username}`).
//
// Rationale (same as avatar — Bug 6 SPEC-049): the PUT /auth/me endpoint
// accepts `coverImage` as a URL string (≤2048 chars) but NOT as a base64
// data URL — base64 routinely exceeds Express's 256kb JSON body limit.
// This dedicated endpoint exists to accept the base64 upload via
// multipart without that limit.
async uploadCoverImage(file: File): Promise<{ coverImage: string }> {
  const formData = new FormData();
  formData.append('cover', file);
  return apiFetch<{ coverImage: string }>('/auth/me/cover-image', {
    method: 'POST',
    body: formData,
    skipJsonStringify: true,
  });
},
// SPEC-095: clear the cover image. The DELETE endpoint sets
// `user.profile.coverImage = ''` server-side. Returns `{ coverImage: '' }`
// so the caller can update local state without an extra refresh().
async deleteCoverImage(): Promise<{ coverImage: '' }> {
  return apiFetch<{ coverImage: '' }>('/auth/me/cover-image', {
    method: 'DELETE',
  });
},
// MTRX-041 / CONF-006 fix: User Branding upload (POST /auth/me/branding).
// Accepts optional logo (Oro+) + backgroundImage (Platino+) + colors
// (any tier). Mirrors the room branding upload pattern.
async uploadUserBranding(
  payload: {
    logo?: File;
    backgroundImage?: File;
    primaryColor?: string;
    secondaryColor?: string;
    removeLogo?: boolean;
    removeBackgroundImage?: boolean;
  },
): Promise<{ branding: { roomLogoUrl: string; roomPrimaryColor: string; roomSecondaryColor: string; roomBackgroundUrl: string } }> {
  const formData = new FormData();
  if (payload.logo) formData.append('logo', payload.logo);
  if (payload.backgroundImage) formData.append('backgroundImage', payload.backgroundImage);
  if (payload.primaryColor !== undefined) formData.append('primaryColor', payload.primaryColor);
  if (payload.secondaryColor !== undefined) formData.append('secondaryColor', payload.secondaryColor);
  if (payload.removeLogo) formData.append('removeLogo', 'true');
  if (payload.removeBackgroundImage) formData.append('removeBackgroundImage', 'true');
  return apiFetch('/auth/me/branding', {
    method: 'POST',
    body: formData,
    skipJsonStringify: true,
  });
},
// P-108 (SPEC-090 §1.5 — fix de raíz de P-082 / L-67): fetch the full
// user branding subdoc ON-DEMAND — including the binary assets
// (`roomLogoUrl` + `roomBackgroundUrl`) that `toPublic()` excludes
// since commit `ae708084c` (P-082 ROOT FIX). The frontend calls this
// ONLY when the binary assets are actually needed — currently from
// `BrandingEditor` (when the dialog opens, to show the existing logo
// + background) and from `/app/conferences` (conference room setup,
// to render the user's default branding preview). Keeping this
// fetch out of the global `useAuth().refresh()` path keeps the login
// response minimal (< 50KB per L-67) regardless of how large the
// stored PNGs are.
async getUserBranding(): Promise<{
  branding: {
    roomLogoUrl: string;
    roomPrimaryColor: string;
    roomSecondaryColor: string;
    roomBackgroundUrl: string;
  };
}> {
  return apiFetch('/users/me/branding', {
    method: 'GET',
  });
},
// SPEC-AI-CONTEXT-FILE-STORAGE PR5 (R1 PR4 Condition #1 per §13 PR5 acceptance):
// PATCH /auth/me/branding — accept CDN URL strings (NOT multipart files) for
// `roomLogoUrl` + `roomBackgroundUrl`. Closes the PR4 Deviation #1: in PR4,
// the StorageImagePicker uploaded to user-file storage + showed the cdnUrl
// in a toast, but there was no BE endpoint to PERSIST the cdnUrl string to
// `User.branding.roomLogoUrl` / `roomBackgroundUrl` (the existing
// `POST /auth/me/branding` only accepted multipart `File` objects).
//
// PR5 ADDS this PATCH endpoint so the picker's `onSelect(file)` callback
// can persist the cdnUrl directly. The existing `uploadUserBranding`
// (POST multipart) method stays for backwards compat during the migration
// window — once the FE has fully switched to this PATCH path, the POST
// multipart endpoint will be deprecated.
//
// The shape mirrors the BE Zod schema `updateBrandingUrlsSchema`
// (`backend/src/features/auth/controllers/schemas.ts`): both fields
// optional but at least one MUST be provided, both must be `https://` URLs
// (max 500 chars). The response is the updated branding object.
async updateBrandingUrls(data: {
  roomLogoUrl?: string;
  roomBackgroundUrl?: string;
}): Promise<{ roomLogoUrl: string; roomBackgroundUrl: string }> {
  return apiFetch('/auth/me/branding', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
},
// SPEC-008 §3.2.2 — Change password (POST /auth/change-password, endpoint #11)
async changePassword(data: ChangePasswordInput): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
},
// SPEC-010 §3.1.1 — Setup 2FA (POST /auth/setup-2fa, endpoint #12)
async setup2FA(): Promise<Setup2FAResult> {
  return apiFetch<Setup2FAResult>('/auth/setup-2fa', {
    method: 'POST',
  });
},
// SPEC-010 §3.1.2 — Verify 2FA code (POST /auth/verify-2fa)
// NOTE: This endpoint requires a temp_2fa token (issued by /auth/login when
// 2FA is enabled). Used ONLY in the login flow.
async verify2FA(code: string): Promise<Verify2FAResult> {
  return apiFetch<Verify2FAResult>('/auth/verify-2fa', {
    method: 'POST',
    // AUDIT-1-BUG-008: skipRefresh=true — the verify-2fa endpoint uses a
    // temp_2fa token (NOT a real access token) issued by /auth/login when
    // 2FA is required. The temp_2fa token has type='temp_2fa' (not
    // 'access'), so it CANNOT be refreshed via /auth/refresh (that endpoint
    // expects a refresh cookie, not a temp_2fa Bearer). Pre-fix, if the
    // verify-2fa call returned 401 (e.g. wrong TOTP code), the apiFetch
    // wrapper would try to refresh the token via /auth/refresh — which
    // would fail (no refresh cookie set yet for 2FA-pending sessions) →
    // dispatch `profileia:session-expired` → log the user out + redirect
    // to /login. The user would have to start the login flow over. Post-
    // fix, `skipRefresh: true` tells apiFetch to NOT attempt a refresh on
    // 401 — the 401 is surfaced to the caller (the login page's
    // onVerify2FA handler) which displays "Código 2FA inválido" + lets the
    // user retry without losing their 2FA-pending session. Spec ref:
    // AUDIT-1-BUG-008, SPEC-010 §3.1.3 (2FA login flow).
    skipRefresh: true,
    body: JSON.stringify({ code }),
  });
},
// Bug #35 fix (R3 CORROBORACIÓN QA): Verify 2FA for financial operations.
// Uses the user's existing access token (NOT temp_2fa). Used by the wallet
// page when a user wants to withdraw and needs to re-verify 2FA. Calls
// the new /auth/verify-2fa-financial endpoint which sets the 5-min Redis
// key (requireRecent2FA middleware passes for next /withdraw call).
async verify2FAFinancial(code: string): Promise<{ verified: boolean; enabled: boolean }> {
  const res = await apiFetch<{ verified: boolean; enabled: boolean }>('/auth/verify-2fa-financial', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return res;
},
// BUG-FE-002 (SPEC-036): Verify 2FA setup from Settings page.
// Uses the user's existing access token (NOT temp_2fa — that's login flow).
// Called AFTER /auth/setup-2fa (which generated the secret + backup codes).
// The service verifies TOTP against user.twoFactor.secret, sets enabled=true
// on first-time setup, and calls mark2faVerified for the 5-min financial
// window. Backup codes were already returned by setup2FA — frontend keeps
// them in state and shows them after this verify succeeds.
async verify2FASetup(code: string): Promise<{ verified: boolean; enabled: boolean }> {
  const res = await apiFetch<{ verified: boolean; enabled: boolean }>('/auth/2fa/setup/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return res;
},
// SPEC-010 §3.2.3 — Forgot password (POST /auth/forgot-password)
async forgotPassword(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/forgot-password', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: JSON.stringify({ email }),
  });
},
// SPEC-010 §3.2.4 — Reset password (POST /auth/reset-password)
async resetPassword(data: ResetPasswordInput): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: JSON.stringify(data),
  });
},
// SPEC-010 §3.2.1 — Verify email (POST /auth/verify-email)
// BUG-FE-001 (SPEC-036): endpoint público. Email + token van en body.
// En éxito: devuelve { verified, user, tokens } (auto-login).
async verifyEmail(
  email: string,
  token: string,
): Promise<{ verified: boolean; user: PublicUser; tokens: { accessToken: string; expiresIn: number; tokenType: 'Bearer' } }> {
  const res = await apiFetch<{
    verified: boolean;
    user: PublicUser;
    tokens: { accessToken: string; expiresIn: number; tokenType: 'Bearer' };
  }>('/auth/verify-email', {
    method: 'POST',
    auth: false,
    skipRefresh: true,
    body: JSON.stringify({ email, token }),
  });
  // Persistir access token en memoria (auto-login — refresh va en cookie)
  if (res.tokens?.accessToken) tokenStore.setAccess(res.tokens.accessToken);
  return res;
},
// SPEC-010 §3.2.2 — Resend email verification (POST /auth/resend-verification)
async resendVerification(): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/resend-verification', {
    method: 'POST',
  });
},
// SPEC-011 §3.1 — Get network tree (GET /network, endpoint #15)
// FE-010 (Bloque D — QA Co-founder Sprint): added optional `signal`
// parameter (mirrors `api.getWallet(signal)` and `api.me(signal)`).
// The network page (`app/network/page.tsx`) creates an AbortController
// and aborts it on unmount, but previously the signal was NEVER passed
// to this method — so the abort was a no-op and the fetch continued,
// eventually calling `setState` on an unmounted component.
//
// Sprint 113 fix: override the default 8s READ_TIMEOUT_MS to 30s for
// /network. The endpoint runs a BFS over matrix_nodes (6 levels, up to
// 126 nodes) + per-level User.find batch queries + User.countDocuments
// for the directReferrals stat. On a cold Redis cache (first load after
// deploy, or after the 60s TTL expires), the cumulative latency can
// exceed 8s — the frontend would abort with a TIMEOUT error + show
// "La solicitud tardó demasiado" even though the backend was about to
// respond. Post-fix: 30s timeout gives the backend enough room to
// complete the BFS + populate the Redis cache. Subsequent loads hit
// the cache (60s TTL) and respond in <500ms. §6 Anti-Invención: pure
// timeout tuning — no business logic.
async getNetwork(signal?: AbortSignal, depth?: number): Promise<NetworkTree> {
  // Sprint 119 perf: optional `depth` param for progressive loading.
  // Clients pass `depth=2` for a fast initial load (just direct referrals
  // + level 2), then call again with `depth=6` when the user expands
  // deeper levels. Default: 6 (full tree — backward compat).
  const query = depth ? `?depth=${depth}` : '';
  return apiFetch<NetworkTree>(`/network${query}`, { signal, timeoutMs: 30_000 });
},
// SPEC-011 §3.2 — Get wallet (GET /wallet, endpoint #16)
async getWallet(signal?: AbortSignal): Promise<WalletData> {
  return apiFetch<WalletData>('/wallet', { signal });
},
// H-05: Change wallet address (POST /wallet/address-change, 2FA gated).
// Requires requireRecent2FA middleware — user must verify 2FA first.
// C-05 fix: backend expects { address, chain } (field is 'address', not
// 'newAddress'; no 'code' field since 2FA is pre-verified via middleware).
// C-06 fix: backend returns { previousAddress, newAddress, chain }.
async changeWalletAddress(data: { chain: string; address: string }): Promise<{ previousAddress: string; newAddress: string; chain: string }> {
  return apiFetch<{ previousAddress: string; newAddress: string; chain: string }>('/wallet/address-change', {
    method: 'POST',
    body: JSON.stringify(data),
  });
},
// P-274: Per-chain deposit wallets (GamesP2P pattern).
// POST /auth/deposit-wallets — save a wallet for a specific chain (no 2FA).
// DELETE /auth/deposit-wallets/:chain — remove a wallet for a chain.
async saveDepositWallet(chain: string, address: string): Promise<{
  depositWallets: Record<string, string | null>;
  defaultPaymentWallet: string | null;
}> {
  return apiFetch('/auth/deposit-wallets', {
    method: 'POST',
    body: JSON.stringify({ chain, address }),
  });
},
async removeDepositWallet(chain: string): Promise<{
  depositWallets: Record<string, string | null>;
  defaultPaymentWallet: string | null;
}> {
  return apiFetch(`/auth/deposit-wallets/${chain}`, {
    method: 'DELETE',
  });
},
// H-06: Payout history for the current user (GET /payout/history).
// Returns paginated list of user's payout requests.
// H-11 fix: backend expects ?offset=N&limit=M (not ?page=N&limit=M).
// Convert page to offset internally: offset = (page - 1) * limit.
async getPayoutHistory(params?: { page?: number; limit?: number }, signal?: AbortSignal): Promise<Paginated<PayoutDto>> {
  const query = new URLSearchParams();
  const limit = params?.limit ?? 20;
  const offset = ((params?.page ?? 1) - 1) * limit;
  query.set('offset', String(offset));
  query.set('limit', String(limit));
  const qs = query.toString();
  return apiFetch<Paginated<PayoutDto>>(qs ? `/payout/history?${qs}` : '/payout/history', { signal });
},
// M-07: Available deposit chains (GET /deposit/chains).
// Returns chain configs (name, contract, confirmationsRequired, etc.)
// for deposit UI. Enables future UI even if not currently consumed.
async getDepositChains(signal?: AbortSignal): Promise<DepositChainInfo[]> {
  return apiFetch<DepositChainInfo[]>('/deposit/chains', { signal });
},
// M-07: Deposit history for the current user (GET /deposit/history).
// Returns paginated list of user's deposits.
// H-12 fix: backend expects ?offset=N&limit=M (not ?page=N&limit=M).
// Convert page to offset internally: offset = (page - 1) * limit.
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
// 4F-MED-001: return type changed from Paginated<DepositDto> (9-value
// AdminDepositStatus) to Paginated<UserDepositDto> (6-value DepositStatus).
// User-facing endpoints never return admin-only statuses.
async getDepositHistory(params?: { page?: number; limit?: number }, signal?: AbortSignal): Promise<Paginated<UserDepositDto>> {
  const query = new URLSearchParams();
  const limit = params?.limit ?? 20;
  const offset = ((params?.page ?? 1) - 1) * limit;
  query.set('offset', String(offset));
  query.set('limit', String(limit));
  const qs = query.toString();
  return apiFetch<Paginated<UserDepositDto>>(qs ? `/deposit/history?${qs}` : '/deposit/history', { signal });
},
// SPEC-011 §3.3 — Get conferences (GET /conferences, endpoint #17)
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getConferences(signal?: AbortSignal): Promise<ConferencesData> {
  return apiFetch<ConferencesData>('/conferences', { signal });
},
// SPEC-011 §3.3 — Create conference (POST /conferences, endpoint #18)
async createConference(data: CreateConferenceInput): Promise<ConferencesData> {
  return apiFetch<ConferencesData>('/conferences', {
    method: 'POST',
    body: JSON.stringify(data),
  });
},
// BUG-FE-003 (SPEC-036): GET /conferences/:roomId — room status.
// Used by /app/conferences/[roomId] page to validate that the room exists
// + user has access before rendering the ConferenceRoom component.
// Returns 404 if room not found, 403 if access denied.
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getConference(roomId: string, signal?: AbortSignal): Promise<ConferenceRoomResponse> {
  return apiFetch<ConferenceRoomResponse>(`/conferences/${encodeURIComponent(roomId)}`, { signal });
},
// MTRX-040 / CONF-006: PATCH /conferences/:roomId/settings — host updates
// the welcome message + waiting room flag. The endpoint is host-only
// (backend re-verifies hostUserId === req.user.sub atomically). Returns
// the updated fields so the UI can sync local state without a refetch.
async updateRoomSettings(
  roomId: string,
  patch: { welcomeMessage?: string; waitingRoomEnabled?: boolean },
): Promise<{ welcomeMessage: string; waitingRoomEnabled: boolean }> {
  return apiFetch(`/conferences/${encodeURIComponent(roomId)}/settings`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
},
// MTRX-040 / CONF-006: PUT /conferences/:roomId/branding — host uploads
// logo (Oro+) and/or backgroundImage (Platino+). Either field may be
// omitted to keep the existing value. `removeLogo` / `removeBackgroundImage`
// query flags (sent via FormData) let the host clear a previously-uploaded
// image without re-uploading. Returns the new branding object.
async uploadRoomBranding(
  roomId: string,
  files: { logo?: File; backgroundImage?: File },
  options?: { removeLogo?: boolean; removeBackgroundImage?: boolean },
): Promise<{ branding: { logoUrl?: string; backgroundImageUrl?: string } }> {
  const formData = new FormData();
  if (files.logo) formData.append('logo', files.logo);
  if (files.backgroundImage) formData.append('backgroundImage', files.backgroundImage);
  if (options?.removeLogo) formData.append('removeLogo', 'true');
  if (options?.removeBackgroundImage) formData.append('removeBackgroundImage', 'true');
  return apiFetch(`/conferences/${encodeURIComponent(roomId)}/branding`, {
    method: 'PUT',
    body: formData,
    skipJsonStringify: true,
  });
},
// BUG-FE-005 (SPEC-036): GET /interviews/:roomId — interview status.
// Used by /app/entrevista/[roomId] page to validate that the interview
// exists + user has access before rendering the post-MVP banner.
// Returns 404 if interview not found, 403 if access denied.
// Spec ref: SPEC-NEGOCIO §1.2 (Interview Room — post-MVP),
//           SPEC-024 §3.3 (Interview Room Page).
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getInterview(roomId: string, signal?: AbortSignal): Promise<InterviewRoomResponse> {
  return apiFetch<InterviewRoomResponse>(`/interviews/${encodeURIComponent(roomId)}`, { signal });
},
// ADR-056 + ADR-059 PR #6 (Task 14 cleanup): el método `getHlsPlaylist`
// fue ELIMINADO. HLS Spectator Mode es dead code per ADR-056 (HLS REJECTED —
// WebRTC+SFU es arquitectura canónica permanente). Los spectators SIEMPRE
// usan VideoGrid + SFU consumers (no HLS playlist URL).
// ─── Analytics (SPEC-035 §2.2 Track B — Estadísticas Avanzadas) ──────────
// §6 Anti-Invención: descriptive visualization of existing data only.
// Rate limited by global apiRateLimiter (§9.7: 100 req/min/IP).
//
// A-P1-1 fix (Sprint 97 QA /app/analytics — TRAMPA P-035 closed): the
// 3 analytics response shapes are now imported from `@profileia/shared`
// (single source of truth) instead of being re-declared inline. The
// backend service exports the same interfaces, so any field rename or
// addition is caught at compile-time on both ends.
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getNetworkAnalytics(signal?: AbortSignal): Promise<NetworkStats> {
  return apiFetch<NetworkStats>('/analytics/network', { signal });
},
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getWalletAnalytics(signal?: AbortSignal): Promise<WalletStats> {
  return apiFetch<WalletStats>('/analytics/wallet', { signal });
},
// D-013 fix (SPEC-043 Batch D): added optional `signal` param.
async getConferenceAnalytics(signal?: AbortSignal): Promise<ConferenceStats> {
  return apiFetch<ConferenceStats>('/analytics/conferences', { signal });
},
};

